import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import {
    BsArrowCounterclockwise,
    BsCheck2,
    BsChevronDown,
    BsChevronUp,
    BsXLg,
} from "react-icons/bs"
import { LuLoader } from "react-icons/lu";

import { color_green, color_grey_light } from "../../constants/colors"
import { englishLevels, getTrialRecommendedLevel } from "../../constants/levels"
import { useDispatch, useSelector } from "react-redux";
import { updateStudent } from "../../store/StudentSlice";
import axios from "axios"
import Config from "../../Config"
import { useNavigate } from "react-router-dom";
import ModalQuestion from "./ModalQuestion";
import { useWindowDimensions } from "../../utils";
import getRussianNumberEnding, { getCompletedLessonLabel } from "../../utilities/rusNumbers";
import {
    AllProgramButton,
    ExpandLessonsButton,
    HiddenLessonsDivider,
    DeleteButton,
    EmptyProgram,
    HeaderActions,
    LessonBody,
    LessonCard,
    LessonList,
    LessonMeta,
    LessonRow,
    LessonTitle,
    MetaChip,
    NowBadge,
    ProgramCard,
    ProgramHeaderCopy,
    ProgramHeaderRow,
    ProgramProgressText,
    ProgramTitle,
    ProgressFill,
    ProgressTrack,
    RemovedLessonInfo,
    RemovedLessonItem,
    RemovedLessonName,
    RemovedLessonsButton,
    RemovedLessonsHeader,
    RemovedLessonsPanel,
    RemovedLessonsTitle,
    RestoreAllButton,
    RestoreLessonButton,
    Rail,
    RailDot,
    RailLine,
    StatusButton,
    ThumbWrap,
    TrialActionCopy,
    TrialActionPanel,
    TrialLevelButton,
    TrialLevelButtons,
    UndoBar,
    UndoButton,
    UndoCopy,
} from "./StudentLessonsProgram.styles";
import { useI18n } from "../../i18n/I18nProvider";

const WIDE_DESKTOP_WIDTH = 1700;
const INITIAL_PAST_LESSONS = 2;
const INITIAL_NEXT_LESSONS = 5;
const LESSONS_PAGE_SIZE = 5;

const getFocusIndex = (lessons) => {
    const currentIndex = lessons.findIndex((lesson) => !lesson.is_done);
    return currentIndex === -1 ? Math.max(lessons.length - 1, 0) : currentIndex;
};

const buildInitialWindow = (lessons) => {
    const focusIndex = getFocusIndex(lessons);
    return {
        start: Math.max(0, focusIndex - INITIAL_PAST_LESSONS),
        end: Math.min(lessons.length, focusIndex + 1 + INITIAL_NEXT_LESSONS),
    };
};

const getLessonTitle = (lesson) =>
    `${lesson.listPosition + 1}. ${lesson.name}`;

const compareLessonsByOrder = (a, b) => {
    const indexA = a.index ?? Number.MAX_SAFE_INTEGER;
    const indexB = b.index ?? Number.MAX_SAFE_INTEGER;
    if (indexA !== indexB) {
        return indexA - indexB;
    }
    return a.material_id - b.material_id;
};

const StudentLesson = ({
    lessonData,
    isCurrent,
    isLocked,
    canToggle,
    isLast,
    isLastVisible,
    isTrials,
    isSelected,
    openInNewWindow,
    onDeleted,
    onSelect,
}) => {
    const { t } = useI18n();
    const student = useSelector(state => state.student);
    const dispatch = useDispatch();
    const [isModalQuestionOpen, setIsModalQuestionOpen] = useState(false);
    const handleOpenQuestion = useCallback((event) => {
        event.stopPropagation();
        setIsModalQuestionOpen(true);
    }, []);

    const handleDelete = useCallback(async (isDelete) => {

        if (isDelete) {
            axios.patch(Config.BACKEND_ADDR + `/program/step/delete/`, {

                id: lessonData.id,
                student_id: lessonData.student_id,
                level_id: lessonData.level_id,
                material_id: lessonData.material_id,
                is_deleted: !lessonData.is_deleted
            }).then(res => {

                let newData = [];


                for (const lesson of student.lessonsProgram) {
                    if (+lesson.id === +res.data.id) {
                        newData = [...newData, res.data];
                    } else {
                        newData = [...newData, lesson];
                    }
                }


                dispatch(updateStudent({
                    "lessonsProgram": newData
                }))
                onDeleted(lessonData);
            })
                .catch(err => { console.warn(err) })
        }

        setIsModalQuestionOpen(false)
    }, [student, lessonData, dispatch, onDeleted]);

    const handleToggleComplete = useCallback(async (event) => {
        event.stopPropagation();
        try {
            const res = await axios.patch(Config.BACKEND_ADDR + `/program/step/done/`, {
                id: lessonData.id,
                student_id: lessonData.student_id,
                level_id: lessonData.level_id,
                material_id: lessonData.material_id,
                is_done: !lessonData.is_done
            });

            if (isTrials || !isLast || lessonData.is_done) {


                let newData = [];

                for (const lesson of student.lessonsProgram) {
                    if (+lesson.id === +res.data.id) {
                        newData = [...newData, res.data];
                    } else {
                        newData = [...newData, lesson];
                    }
                }

                dispatch(updateStudent({
                    "lessonsProgram": newData
                }));

            } else {

                try {
                    let newStudentLevel = 0;
                    if (student.englishLevel !== "Trials") {
                        newStudentLevel = +student.englishLevel + 1;
                    }

                    await axios.patch(Config.BACKEND_ADDR + `/students/`, {
                        student_id: student.studentId,
                        english_skill: "" + newStudentLevel
                    });

                    dispatch(updateStudent({
                        lessonsProgram: []
                    }));

                    const responseProgram = await axios.put(Config.BACKEND_ADDR + "/program/", {
                        student_id: student.studentId,
                        level_name: newStudentLevel,
                    });

                    dispatch(updateStudent({
                        lessonsProgram: responseProgram.data,
                        englishLevel: "" + newStudentLevel
                    }));

                } catch (err) {
                    console.warn(err);
                }
            }
        } catch (err) {
            console.warn(err);
        }
    }, [lessonData.id, lessonData.student_id, lessonData.level_id, lessonData.material_id, lessonData.is_done, dispatch, isLast, isTrials, student.lessonsProgram, student.studentId, student.englishLevel]);

    const handleOpenLesson = useCallback((event) => {
        event?.stopPropagation?.();
        if (lessonData) {
            openInNewWindow(Config.CLOUD_ADDR + "/speakid/" + lessonData.index_html_path);
        } else {
            console.warn("Current material not found for lesson:", lessonData);
        }
    }, [lessonData, openInNewWindow]);

    const handleSelectTrial = useCallback((event) => {
        event.stopPropagation();
        onSelect(lessonData.id);
    }, [lessonData.id, onSelect]);

    const isDone = Boolean(lessonData.is_done);
    const recommendedLevel = isTrials
        ? getTrialRecommendedLevel(lessonData)
        : null;

    return (
        <>
            {isModalQuestionOpen &&
                <ModalQuestion
                    isOpen={isModalQuestionOpen}
                    setIsOpen={setIsModalQuestionOpen}
                    type={"error"}
                    h2={t("Удалить занятие?")}
                    p={t("Занятие скроется из программы. Его можно будет восстановить позже.")}
                    onClose={handleDelete}
                />
            }
            <LessonRow $trials={isTrials}>
                {!isTrials && (
                    <Rail>
                        <RailLine $done={isDone} $current={isCurrent} $last={isLastVisible} />
                        <RailDot $done={isDone} $current={isCurrent} $locked={isLocked} />
                    </Rail>
                )}
                <LessonCard
                    $current={isCurrent}
                    $locked={isLocked}
                    $trials={isTrials}
                    $selected={isSelected}
                    onClick={isTrials ? handleSelectTrial : undefined}
                >
                    <ThumbWrap onClick={handleOpenLesson}>
                        <img
                            src={Config.CLOUD_ADDR + '/speakid/' + lessonData.image_path}
                            alt={lessonData.name || "lesson"}
                            loading={isCurrent || isSelected ? "eager" : "lazy"}
                            decoding="async"
                            fetchPriority={isCurrent || isSelected ? "high" : "auto"}
                        />
                    </ThumbWrap>
                    <LessonBody>
                        <LessonTitle>
                            {getLessonTitle(lessonData)}
                        </LessonTitle>
                        <LessonMeta>
                            {recommendedLevel && (
                                <MetaChip>
                                    {recommendedLevel.name}
                                </MetaChip>
                            )}
                            {isTrials && isSelected && !isDone && <NowBadge>{t("Выбран")}</NowBadge>}
                            {!isTrials && isCurrent && <NowBadge>{t("Сейчас")}</NowBadge>}
                            {isDone && <MetaChip>{t("Пройден")}</MetaChip>}
                        </LessonMeta>
                    </LessonBody>
                    <StatusButton
                        type="button"
                        $done={isDone}
                        disabled={!canToggle}
                        title={
                            canToggle
                                ? (isDone ? t("Снять отметку") : t("Отметить выполненным"))
                                : (isTrials ? t("Сначала выберите это занятие") : t("Сначала завершите текущий урок"))
                        }
                        onClick={handleToggleComplete}
                    >
                        {!isDone && isLocked ?
                            <LuLoader style={{ color: color_grey_light }} />
                            :
                            <BsCheck2 style={{ color: isDone ? color_green : color_grey_light }} />
                        }
                    </StatusButton>
                    <DeleteButton
                        type="button"
                        title={t("Удалить занятие")}
                        onClick={handleOpenQuestion}
                    >
                        <BsXLg />
                    </DeleteButton>
                </LessonCard>
            </LessonRow>
        </>
    )
}

export const StudentLessonsProgram = ({ levels, openInNewWindow }) => {
    const { t } = useI18n();
    const { width: screenWidth } = useWindowDimensions();
    const isDesktop = screenWidth > 1155;

    const navigate = useNavigate();

    const student = useSelector(state => state.student);
    const dispatch = useDispatch();
    const isTrials = String(student.englishLevel).toLowerCase() === "trials";
    const [windowStart, setWindowStart] = useState(0);
    const [windowEnd, setWindowEnd] = useState(0);
    const [showRemovedLessons, setShowRemovedLessons] = useState(false);
    const [lastRemovedLesson, setLastRemovedLesson] = useState(null);
    const [restoringLessonIds, setRestoringLessonIds] = useState(new Set());
    const [selectedTrialLessonId, setSelectedTrialLessonId] = useState(null);
    const [assigningLevel, setAssigningLevel] = useState(null);
    const windowInitializedRef = useRef(false);
    const prevDoneCountRef = useRef(0);

    const handleLessonDeleted = useCallback((lesson) => {
        setLastRemovedLesson(lesson);
    }, []);

    const handleLessonSelect = useCallback((lessonId) => {
        if (isTrials) {
            setSelectedTrialLessonId(lessonId);
        }
    }, [isTrials]);

    const assignTrialResultLevel = useCallback(async (levelIndex) => {
        setAssigningLevel(levelIndex);
        try {
            await axios.patch(Config.BACKEND_ADDR + `/students/`, {
                student_id: student.studentId,
                english_skill: String(levelIndex),
            });
            const response = await axios.put(Config.BACKEND_ADDR + "/program/", {
                student_id: student.studentId,
                level_name: levelIndex,
            });
            dispatch(updateStudent({
                lessonsProgram: response.data,
                englishLevel: String(levelIndex),
            }));
        } catch (error) {
            console.warn("Failed to assign assessed student level:", error);
        } finally {
            setAssigningLevel(null);
        }
    }, [dispatch, student.studentId]);

    const restoreLessons = useCallback(async (lessons) => {
        const lessonsToRestore = lessons.filter(Boolean);
        if (lessonsToRestore.length === 0) {
            return;
        }

        const lessonIds = lessonsToRestore.map((lesson) => lesson.id);
        setRestoringLessonIds((current) => new Set([...current, ...lessonIds]));

        try {
            const results = await Promise.allSettled(
                lessonsToRestore.map((lesson) =>
                    axios.patch(Config.BACKEND_ADDR + `/program/step/delete/`, {
                        id: lesson.id,
                        student_id: lesson.student_id,
                        level_id: lesson.level_id,
                        material_id: lesson.material_id,
                        is_deleted: false,
                    })
                )
            );
            const restoredById = new Map(
                results
                    .filter((result) => result.status === "fulfilled")
                    .map((result) => [result.value.data.id, result.value.data])
            );

            if (restoredById.size > 0) {
                dispatch(updateStudent({
                    lessonsProgram: student.lessonsProgram.map((lesson) =>
                        restoredById.get(lesson.id) ?? lesson
                    ),
                }));
                setLastRemovedLesson((lesson) =>
                    lesson && restoredById.has(lesson.id) ? null : lesson
                );
            }

            const failedCount = results.length - restoredById.size;
            if (failedCount > 0) {
                console.warn(`Failed to restore ${failedCount} program lesson(s).`);
            }
        } finally {
            setRestoringLessonIds((current) => {
                const next = new Set(current);
                lessonIds.forEach((id) => next.delete(id));
                return next;
            });
        }
    }, [dispatch, student.lessonsProgram]);

    useEffect(() => {
        if (!lastRemovedLesson) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setLastRemovedLesson(null);
        }, 8000);

        return () => window.clearTimeout(timeoutId);
    }, [lastRemovedLesson]);

    useEffect(() => {
        if (student.studentId === -1) {
            return;
        }
        const controller = new AbortController();

        axios.get(Config.BACKEND_ADDR + `/program/level/`, {
            params: {
                student_id: student.studentId,
                level_name: student.englishLevel,
            },
            signal: controller.signal,
        })
            .then(res => {
                dispatch(updateStudent({
                    "lessonsProgram": res.data
                }))
            })
            .catch(err => {
                if (!axios.isCancel(err)) {
                    console.warn(err);
                }
            })

        return () => controller.abort();
    }, [student.studentId, student.englishLevel, dispatch])

    useEffect(() => {
        windowInitializedRef.current = false;
        prevDoneCountRef.current = 0;
        setWindowStart(0);
        setWindowEnd(0);
        setShowRemovedLessons(false);
        setLastRemovedLesson(null);
        setSelectedTrialLessonId(null);
    }, [student.studentId, student.englishLevel]);

    const program = useMemo(() => {
        const currentLevel = levels.find(
            (level) => String(level.name) === String(student.englishLevel)
        );
        const materialsById = new Map(
            (currentLevel?.materials ?? []).map((material) => [material.id, material])
        );

        return student.lessonsProgram
            .map((lesson) => {
                const material = materialsById.get(lesson.material_id);
                if (!material) {
                    return null;
                }
                return {
                    ...material,
                    ...lesson,
                    index: material.index ?? lesson.index ?? null,
                };
            })
            .filter(Boolean)
            .sort(compareLessonsByOrder)
            .map((lesson, position) => ({
                ...lesson,
                listPosition: position,
            }));
    }, [student.lessonsProgram, levels, student.englishLevel]);

    const visibleProgram = useMemo(
        () => program.filter((el) => !el.is_deleted),
        [program]
    );
    const removedProgram = useMemo(
        () => program.filter((el) => el.is_deleted),
        [program]
    );
    const doneCount = visibleProgram.filter((el) => el.is_done).length;
    const totalCount = visibleProgram.length;
    const progressPercent = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;
    const selectedCompletedTrial = visibleProgram.find(
        (lesson) => lesson.id === selectedTrialLessonId && lesson.is_done
    );
    const lastCompletedTrial = [...visibleProgram].reverse().find((lesson) => lesson.is_done);
    const assessedTrialLesson = selectedCompletedTrial ?? lastCompletedTrial;
    const recommendedTrialLevel = getTrialRecommendedLevel(assessedTrialLesson);

    useEffect(() => {
        if (totalCount === 0) {
            windowInitializedRef.current = false;
            prevDoneCountRef.current = 0;
            setWindowStart(0);
            setWindowEnd(0);
            return;
        }

        if (!windowInitializedRef.current) {
            const { start, end } = buildInitialWindow(visibleProgram);
            setWindowStart(start);
            setWindowEnd(end);
            prevDoneCountRef.current = doneCount;
            windowInitializedRef.current = true;
            return;
        }

        if (doneCount > prevDoneCountRef.current) {
            setWindowEnd((end) => Math.min(totalCount, end + 1));
        }

        prevDoneCountRef.current = doneCount;
    }, [visibleProgram, totalCount, doneCount]);

    const currentLessonIndex = visibleProgram.findIndex((lesson) => !lesson.is_done);
    const lastCompletedLessonIndex = visibleProgram.reduce(
        (lastIndex, lesson, index) => lesson.is_done ? index : lastIndex,
        -1
    );
    const displayedProgram = isTrials
        ? visibleProgram
        : visibleProgram.slice(windowStart, windowEnd);
    const hiddenPastCount = isTrials ? 0 : windowStart;
    const hiddenNextCount = isTrials ? 0 : totalCount - windowEnd;
    const lastProgramLessonId = visibleProgram[visibleProgram.length - 1]?.id;

    const programElements = displayedProgram.map((lesson, displayedIndex) => {
        const fullIndex = windowStart + displayedIndex;
        const regularFullIndex = isTrials ? displayedIndex : fullIndex;
        const lessonIsCurrent =
            !isTrials && regularFullIndex === currentLessonIndex;
        const lessonIsLocked =
            !isTrials &&
            currentLessonIndex !== -1 &&
            regularFullIndex > currentLessonIndex;
        const canToggle =
            isTrials
                ? selectedTrialLessonId === lesson.id || Boolean(lesson.is_done)
                : lessonIsCurrent ||
                    (Boolean(lesson.is_done) &&
                        regularFullIndex === lastCompletedLessonIndex);

        return (
            <StudentLesson
                key={lesson.id}
                lessonData={lesson}
                isCurrent={lessonIsCurrent}
                isLocked={lessonIsLocked}
                canToggle={canToggle}
                isLast={lesson.id === lastProgramLessonId}
                isLastVisible={displayedIndex === displayedProgram.length - 1}
                isTrials={isTrials}
                isSelected={selectedTrialLessonId === lesson.id}
                openInNewWindow={openInNewWindow}
                onDeleted={handleLessonDeleted}
                onSelect={handleLessonSelect}
            />
        );
    });

    return (
        <ProgramCard $width={screenWidth >= WIDE_DESKTOP_WIDTH ? "100%" : (isDesktop ? 600 : "100%")}>
            <ProgramHeaderRow>
                <ProgramHeaderCopy>
                    <ProgramTitle>
                        {isTrials ? t("Пробные занятия") : t("Программа занятий")}
                    </ProgramTitle>
                    <ProgramProgressText>
                        {isTrials
                            ? (doneCount
                                ? t("Проведено пробных занятий: {{count}}", { count: doneCount })
                                : t("Выберите одно занятие для диагностики уровня"))
                            : totalCount
                            ? t("{{doneCount}} из {{totalCount}} пройдено", { doneCount, totalCount })
                            : program.length
                                ? t("Все занятия скрыты")
                            : t("Программа пока не назначена")}
                    </ProgramProgressText>
                </ProgramHeaderCopy>
                <HeaderActions>
                    {removedProgram.length > 0 && (
                        <RemovedLessonsButton
                            type="button"
                            $active={showRemovedLessons}
                            aria-expanded={showRemovedLessons}
                            onClick={() => setShowRemovedLessons((visible) => !visible)}
                        >
                            <BsArrowCounterclockwise />
                            {t("Скрыто: {{count}}", { count: removedProgram.length })}
                        </RemovedLessonsButton>
                    )}
                    <AllProgramButton
                        type="button"
                        onClick={() => {
                            navigate('/service/program/');
                        }}
                    >
                        {t("Вся программа")}
                    </AllProgramButton>
                </HeaderActions>
            </ProgramHeaderRow>
            {!isTrials && totalCount > 0 && (
                <ProgressTrack>
                    <ProgressFill $percent={progressPercent} />
                </ProgressTrack>
            )}
            {isTrials && doneCount > 0 && (
                <TrialActionPanel>
                    <TrialActionCopy>
                        <strong>{t("Диагностика проведена")}</strong>
                        <span>
                            {recommendedTrialLevel
                                ? t("Занятие «{{lessonName}}» предназначено для программы {{levelName}}. Подтвердите уровень или выберите другой.", { lessonName: assessedTrialLesson.name, levelName: recommendedTrialLevel.name })
                                : t("Назначьте подходящий уровень или выберите другое пробное занятие ниже.")}
                        </span>
                    </TrialActionCopy>
                    <TrialLevelButtons>
                        {englishLevels.map((levelName, levelIndex) => (
                            <TrialLevelButton
                                key={levelName}
                                type="button"
                                disabled={assigningLevel !== null}
                                $recommended={recommendedTrialLevel?.index === levelIndex}
                                onClick={() => assignTrialResultLevel(levelIndex)}
                            >
                                {assigningLevel === levelIndex
                                    ? t("Назначаем…")
                                    : `Level ${levelIndex} · ${levelName}${recommendedTrialLevel?.index === levelIndex ? ` · ${t("рекомендуем")}` : ""}`}
                            </TrialLevelButton>
                        ))}
                    </TrialLevelButtons>
                </TrialActionPanel>
            )}
            {showRemovedLessons && removedProgram.length > 0 && (
                <RemovedLessonsPanel>
                    <RemovedLessonsHeader>
                        <div>
                            <RemovedLessonsTitle>{t("Скрытые занятия")}</RemovedLessonsTitle>
                            <ProgramProgressText>
                                {t("Можно вернуть одно занятие или восстановить все")}
                            </ProgramProgressText>
                        </div>
                        <RestoreAllButton
                            type="button"
                            disabled={restoringLessonIds.size > 0}
                            onClick={() => restoreLessons(removedProgram)}
                        >
                            {t("Восстановить все")}
                        </RestoreAllButton>
                    </RemovedLessonsHeader>
                    {removedProgram.map((lesson) => (
                        <RemovedLessonItem key={lesson.id}>
                            <RemovedLessonInfo>
                                <RemovedLessonName>{getLessonTitle(lesson)}</RemovedLessonName>
                                {lesson.is_done && (
                                    <ProgramProgressText>{t("Было пройдено")}</ProgramProgressText>
                                )}
                            </RemovedLessonInfo>
                            <RestoreLessonButton
                                type="button"
                                disabled={restoringLessonIds.has(lesson.id)}
                                onClick={() => restoreLessons([lesson])}
                            >
                                <BsArrowCounterclockwise />
                                {restoringLessonIds.has(lesson.id)
                                    ? t("Возвращаем…")
                                    : t("Вернуть")}
                            </RestoreLessonButton>
                        </RemovedLessonItem>
                    ))}
                </RemovedLessonsPanel>
            )}
            <LessonList>
                {hiddenPastCount > 0 && (
                    <HiddenLessonsDivider>
                        <ExpandLessonsButton
                            type="button"
                            onClick={() => setWindowStart((start) =>
                                Math.max(0, start - LESSONS_PAGE_SIZE)
                            )}
                        >
                            <BsChevronUp />
                            {t("Ещё {{count}} {{lessonLabel}}", { count: hiddenPastCount, lessonLabel: t(getCompletedLessonLabel(hiddenPastCount)) })}
                        </ExpandLessonsButton>
                    </HiddenLessonsDivider>
                )}
                {totalCount > 0 ? programElements : (
                    <EmptyProgram>{t("Добавьте занятия из раздела «Вся программа».")}</EmptyProgram>
                )}
                {hiddenNextCount > 0 && (
                    <HiddenLessonsDivider>
                        <ExpandLessonsButton
                            type="button"
                            onClick={() => setWindowEnd((end) =>
                                Math.min(totalCount, end + LESSONS_PAGE_SIZE)
                            )}
                        >
                            {t("Ещё {{count}} урок{{ending}}", { count: hiddenNextCount, ending: getRussianNumberEnding(hiddenNextCount) })}
                            <BsChevronDown />
                        </ExpandLessonsButton>
                    </HiddenLessonsDivider>
                )}
            </LessonList>
            {lastRemovedLesson && (
                <UndoBar role="status" aria-live="polite">
                    <UndoCopy>
                        <strong>{t("Занятие скрыто")}</strong>
                        <span>{lastRemovedLesson.name}</span>
                    </UndoCopy>
                    <UndoButton
                        type="button"
                        disabled={restoringLessonIds.has(lastRemovedLesson.id)}
                        onClick={() => restoreLessons([lastRemovedLesson])}
                    >
                        <BsArrowCounterclockwise />
                        {t("Отменить")}
                    </UndoButton>
                </UndoBar>
            )}
        </ProgramCard>
    )
}
